// @ts-nocheck
/* eslint-disable @typescript-eslint/no-shadow */
import {
  Box, Divider, List,
} from '@mui/material';
import React, { useState, useEffect, ReactElement } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import _, { cloneDeep } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { SortingRule } from 'react-table';
import { CustomAccordion } from '../../shared/material_components';
import { formsApiClient } from '../../services/fetch/forms-api-client';
import { useApp } from '../../hooks/use-app';
import useSystemApp from '../../hooks/use-system-app';
import TaskLink from './task-link';
import TaskLinkPicker from './task-link-picker';
import { LinkedRecord } from '../../shared/material_components/linked-cell';
import { defaultFilters } from '../../record-viewer/filters';
import RecordPicker from '../../record-viewer/record-picker';
import { FilterCondition, RecordFilters, RestApiForm } from '../types';
import { SystemType } from '../../record-viewer/types';
import { useForm } from '../../hooks';
import { createButtonLabel } from '../tasks.helper';
import { ParentRecordState } from '../../record-editor';
import { PickerItemHandler } from '../../editor/fields/helpers/editor-fields-helper';
import { Record } from '../../editor/record.types';
import { Instruction } from '../../hooks/use-form-fields';
import { useGetRecordsQuery } from '../../services/records.service';

export interface QueryRecords {
  records: Record<string, any>[],
  // eslint-disable-next-line camelcase
  total_count: number,
}
interface Props {
  hidden?: boolean,
  sectionKey: string,
  title: string,
  taskIDs: string[],
  valuesForConditions?: any[],
  linkSelectedRecords: (selectedTaskIDs: any[], parentRecordState: ParentRecordState) => void;
  onEditTask?: (
    record?: any,
    formData?: any,
    shouldLinkToParent?: boolean,
    isAppLink?: boolean,
    specialInstructions?: Instruction[]
  ) => void,
  onChange?: () => void,
  formAccessible?: boolean,
  disabled?: boolean,
  allowMultiple?: boolean,
  allowSelectExisting?: boolean,
  allowCreating?: boolean,
  allowDeleting?: boolean,
  linkedRecordFormID?: string,
  listItem?: (
    linkedRecord: LinkedRecord,
    openRecordModal: (record: LinkedRecord) => void,
    restForm?: RestApiForm,
  ) => ReactElement,
  isAppLink?: boolean,
  createLabel?: string
  pickerItemHandler: PickerItemHandler,
}

const PICKER_PAGE_SIZE = 30;

// eslint-disable-next-line react/function-component-definition
const TaskLinkField: React.FC<Props> = ({
  hidden = false,
  sectionKey,
  title,
  taskIDs,
  valuesForConditions = [],
  linkSelectedRecords,
  onEditTask = () => { },
  isAppLink,
  allowCreating,
  linkedRecordFormID,
  createLabel = createButtonLabel.task,
  listItem = (task) => <TaskLink key={task.id} task={task} onClick={() => { onEditTask(); }} />,
  pickerItemHandler,
  disabled,
}: Props) => {
  const app = useApp();
  const queryClient = useQueryClient();
  const [tasks, setTasks] = useState<LinkedRecord[]>([]);
  const [currentlyLinkedRecords, setCurrentlyLinkedRecords] = useState<string[]>(
    cloneDeep(taskIDs) as string[],
  );

  const [openTaskPicker, setOpenTaskPicker] = useState<boolean>(false);
  const [filters, setFilters] = useState<RecordFilters>({
    globalFilter: '',
    filters: defaultFilters[SystemType.task],
  });
  const [paginatedRecords, setPaginatedRecords] = useState<QueryRecords>({} as QueryRecords);
  const [increment, setIncrement] = useState({ pageIndex: 0, increaseCount: false });
  const { pageIndex, increaseCount } = increment;
  const [sorted] = useState<SortingRule<object>[]>([{ id: '_title', desc: false }]);
  const [parentRecordState, setParentRecordState] = useState<ParentRecordState>(
    {} as ParentRecordState,
  );
  const apiClient = formsApiClient(app?.api?.token);
  const linkForm = isAppLink ? useForm(linkedRecordFormID) : useSystemApp(linkedRecordFormID);
  const { data: linkedRecordFormData, status: formDataStatus } = linkedRecordFormID ? linkForm : { data: null, status: 'loading' };
  const {
    data, isLoading,
  } = useGetRecordsQuery({
    app,
    restForm: linkedRecordFormData?.restForm,
    queryColumns: linkedRecordFormData?.queryColumns,
    recordFilters: filters,
    sortingInstructions: sorted,
    pageIndex,
    pageSize: PICKER_PAGE_SIZE,
    isAppLink,
  });

  const isPickerListAvailable = !isLoading && Array.isArray(data?.records)
    ? data.records.length > 0
    : false;

  useEffect(() => {
    const loadRecords = async () => {
      const requestedTasks = await Promise.allSettled<LinkedRecord>(
        currentlyLinkedRecords
          .map(async (taskID) => apiClient.fetchRecord(
            taskID,
            app,
            linkedRecordFormData?.restForm.form_links,
          )),
      );
      const isFulfilled = (
        promise: PromiseSettledResult<LinkedRecord>,
      ): promise is PromiseFulfilledResult<LinkedRecord> => (
        promise.status === 'fulfilled'
      );

      const loadedTasks = requestedTasks
        .filter(isFulfilled)
        .map((res) => res.value);
      setTasks(loadedTasks);
    };
    const currentTaskIDs = tasks.map((task) => task.id);
    if (currentTaskIDs.length !== currentlyLinkedRecords.length || !currentTaskIDs.every((id) => currentlyLinkedRecords.includes(id))) {
      loadRecords();
    }
  }, [currentlyLinkedRecords]);

  useEffect(() => {
    const parentRecordExists = !pickerItemHandler.allowMultiple
      && currentlyLinkedRecords.length === 1
      && _.isEmpty(parentRecordState.currentParentRecord);
    if (parentRecordExists) {
      const currentParentRecord = tasks
        .find((rc: LinkedRecord) => rc.id === currentlyLinkedRecords[0]);
      setParentRecordState({
        ...parentRecordState,
        currentParentRecord,
        allowMultiple: pickerItemHandler.allowMultiple,
      });
    }
  }, [tasks]);

  useEffect(() => {
    setCurrentlyLinkedRecords(cloneDeep(taskIDs) as string[]);
  }, [taskIDs]);

  useEffect(() => {
    if (data) {
      if (pageIndex && increaseCount) {
        setPaginatedRecords({
          ...paginatedRecords,
          records: paginatedRecords.records.concat(data.records),
        });
        setIncrement({ ...increment, increaseCount: false });
      } else {
        setPaginatedRecords(data);
      }
    }
  }, [data]);

  useEffect(() => {
    setIncrement({ pageIndex: 0, increaseCount: false });
    queryClient.invalidateQueries();
  }, [filters]);

  const recordFilterCallback = (recordFilter: FilterCondition[]) => {
    if (Array.isArray(recordFilter)) {
      setFilters({ ...filters, filters: recordFilter });
    } else {
      setFilters({ ...filters, globalFilter: recordFilter });
    }
  };

  const handleLinkSelectedRecords = async (selectedTaskIDs: any[]) => {
    let newParentRecordState: ParentRecordState = {} as ParentRecordState;
    newParentRecordState = pickerItemHandler
      .selectionHandler(data.records, selectedTaskIDs, parentRecordState).newParentRecordState;
    if (!_.isEqual(newParentRecordState, parentRecordState)) {
      setParentRecordState(newParentRecordState);
    }

    linkSelectedRecords(selectedTaskIDs, newParentRecordState);
  };

  const handlePageIncrement = () => {
    setIncrement({ increaseCount: true, pageIndex: pageIndex + 1 });
  };
  const fieldTitle = !pickerItemHandler.allowMultiple ? title : `${title} (${tasks.length})`;
  return (
    <>
      {!hidden
        && (
          <>
            {formDataStatus === 'success' && (
              <>
                <CustomAccordion sectionKey={sectionKey} label={fieldTitle} sx={{ mt: 1 }}>
                  <Box sx={{ width: '100%' }}>
                    {tasks.length ? (
                      <>
                        <List>
                          {
                            tasks.map((linkedRecord) => {
                              const openRecordModal = (
                                record: any, specialInstructions?: Instruction[],
                              ) => {
                                onEditTask(
                                  record,
                                  linkedRecordFormData,
                                  false,
                                  isAppLink,
                                  specialInstructions,
                                );
                              };
                              return listItem(linkedRecord, openRecordModal, linkedRecordFormData?.restForm);
                            })
                          }
                        </List>
                        <Box ml={-2} mr={-2}>
                          <Divider variant="fullWidth" />
                        </Box>
                      </>
                    ) : (<></>)}
                    <TaskLinkPicker
                      allowCreating={allowCreating}
                      onAddTask={() => onEditTask(null, linkedRecordFormData, true)}
                      onSelectTask={() => setOpenTaskPicker(true)}
                      disabled={Boolean(disabled || !isPickerListAvailable)}
                      onCloseClick={() => setOpenTaskPicker(false)}
                      valuesForQueryConditions={valuesForConditions}
                      createLabel={createLabel}
                      selectRecordsTitle={pickerItemHandler.selectorTitle}
                    />
                  </Box>
                </CustomAccordion>
                { !isLoading && paginatedRecords
                  && (
                    <RecordPicker
                      open={openTaskPicker}
                      onCloseClick={() => setOpenTaskPicker(false)}
                      data={paginatedRecords}
                      onSubmitSelection={handleLinkSelectedRecords}
                      currentlyLinkedRecords={tasks}
                      callback={(e) => recordFilterCallback(e)}
                      form={linkedRecordFormData?.restForm}
                      isLoading={isLoading}
                      handlePageIncrement={handlePageIncrement}
                      pickerItemHandler={pickerItemHandler}
                    />
                  )}
              </>
            )}
            {(formDataStatus === 'loading' || isLoading) && (
              <Box pt={2} display="flex" justifyContent="center">
                <CircularProgress data-testid="task-link-loader" />
              </Box>
            )}
          </>
        )}
    </>
  );
};

export default TaskLinkField;
